import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Drawer,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { sendVoiceMessage } from "../../services/voiceChatApi";

interface VoicePanelProps {
  open: boolean;
  onClose: () => void;
}

const VoicePanel: React.FC<VoicePanelProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [transcript, setTranscript] = useState<string | null>(null);
  const [responseText, setResponseText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSupported, setRecordingSupported] = useState(true);
  const [hasResponseAudio, setHasResponseAudio] = useState(false);
  const [, setTtsError] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const responseAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !navigator.mediaDevices) {
      setRecordingSupported(false);
      return;
    }
    if (!(window as any).MediaRecorder) {
      setRecordingSupported(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setHasResponseAudio(false);
      setTtsError(false);
      if (responseAudioRef.current) {
        responseAudioRef.current.pause();
        responseAudioRef.current = null;
      }
    }
  }, [open]);

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    setIsRecording(false);
  };

  const playResponseAudio = () => {
    const audio = responseAudioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch((e) => console.warn("Play failed:", e));
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        stream.getTracks().forEach((track) => track.stop());
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
          analyserRef.current = null;
        }
        if (audioBlob.size === 0) {
          return;
        }
        setLoading(true);
        setError(null);
        setTranscript(null);
        setResponseText(null);
        setHasResponseAudio(false);
        setTtsError(false);
        if (responseAudioRef.current) {
          responseAudioRef.current.pause();
          responseAudioRef.current = null;
        }
        try {
          const {
            transcript: t,
            message,
            response_audio_base64,
            tts_error: ttsErr,
          } = await sendVoiceMessage(audioBlob, []);
          setTranscript(t || null);
          setResponseText((message?.content as string) || null);
          setTtsError(!!ttsErr);
          if (response_audio_base64) {
            try {
              const binary = atob(response_audio_base64);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: "audio/ogg" });
              const url = URL.createObjectURL(blob);
              const audio = new Audio(url);
              responseAudioRef.current = audio;
              audio.onended = () => {
                URL.revokeObjectURL(url);
              };
              setHasResponseAudio(true);
              audio.play().then(
                () => {},
                (playErr: unknown) => {
                  console.warn("Autoplay zablokowany – kliknij „Odtwórz odpowiedź”:", playErr);
                }
              );
            } catch (decodeErr) {
              console.warn("Dekodowanie audio nie powiodło się:", decodeErr);
              setHasResponseAudio(false);
            }
          }
        } catch (err: unknown) {
          const detail =
            axios.isAxiosError(err) && err.response?.data?.detail
              ? String(err.response.data.detail)
              : err instanceof Error
                ? err.message
                : "Wystąpił błąd podczas przetwarzania nagrania.";
          setError(detail);
        } finally {
          setLoading(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);

      const AudioCtx =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        const audioContext = new AudioCtx();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const silenceThreshold = 0.01;
        const maxSilenceMs = 1500;
        let lastNonSilent = performance.now();

        const checkSilence = () => {
          if (!analyserRef.current || !mediaRecorderRef.current) {
            return;
          }
          analyserRef.current.getByteTimeDomainData(dataArray);
          let sumSquares = 0;
          for (let i = 0; i < dataArray.length; i += 1) {
            const v = (dataArray[i] - 128) / 128;
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / dataArray.length);
          const now = performance.now();
          if (rms > silenceThreshold) {
            lastNonSilent = now;
          }
          if (
            now - lastNonSilent > maxSilenceMs &&
            mediaRecorderRef.current.state === "recording"
          ) {
            stopRecording();
            return;
          }
          rafIdRef.current = requestAnimationFrame(checkSilence);
        };

        rafIdRef.current = requestAnimationFrame(checkSilence);
      }
    } catch (err) {
      setRecordingSupported(false);
      const detail =
        err instanceof Error
          ? err.message
          : "Nie udało się uzyskać dostępu do mikrofonu.";
      setError(detail);
    }
  };

  const handleToggleRecording = () => {
    if (loading || !recordingSupported) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 400 },
          maxWidth: "100%",
          backgroundColor: "#2a2a2a",
          backgroundImage: "none",
        },
      }}
    >
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">{t("voicePanel.title")}</Typography>
          <IconButton onClick={onClose} size="small" aria-label={t("voicePanel.closeAria")} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("voicePanel.instruction")}
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Tooltip
            title={
              !recordingSupported
                ? t("voicePanel.recordingUnavailable")
                : isRecording
                  ? t("voicePanel.stopAndSend")
                  : t("voicePanel.recordQuestion")
            }
          >
            <span>
              <IconButton
                color={isRecording ? "error" : "primary"}
                onClick={handleToggleRecording}
                disabled={loading || !recordingSupported}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                  "&:disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
                }}
              >
                {isRecording ? <MicOffIcon fontSize="large" /> : <MicIcon fontSize="large" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {t("voicePanel.processing")}
            </Typography>
          </Box>
        )}
        {(transcript != null || responseText != null) && !loading && (
          <Box sx={{ flex: 1, overflow: "auto", mb: 2 }}>
            {transcript != null && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>{t("voicePanel.you")}:</strong> {transcript}
              </Typography>
            )}
            {responseText != null && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" component="span">
                  <strong>{t("voicePanel.assistant")}:</strong>
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    "& p": { m: 0, mb: 0.5 },
                    "& p:last-child": { mb: 0 },
                    "& ul, & ol": { m: 0, pl: 2.5 },
                    "& li": { mb: 0.25 },
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                  }}
                >
                  <ReactMarkdown>{responseText}</ReactMarkdown>
                </Box>
              </Box>
            )}
            {responseText != null && (
              <Box sx={{ mt: 1.5 }}>
                {hasResponseAudio ? (
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<PlayArrowIcon />}
                    onClick={playResponseAudio}
                    sx={{ mt: 0.5 }}
                  >
                    {t("voicePanel.playResponse")}
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    {t("voicePanel.textOnlyResponse")}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default VoicePanel;
